import { Box, Button, Container, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { MaterialReactTable } from "material-react-table";
// import {csv} from "papaparse";
import { getColumns } from "./columns";
import NotesModal from "./checkNotes/NotesModal";
import { ChecksContext } from "./ChecksContext";
import { useRef, useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../contexts/UserContext";
import CheckActions from "./CheckActions";
import axios from "axios";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { InternalUser } from "../types/app";
import { fCurrency } from "../utils/formatNumber";
import { getOrderNumberDisplay } from "./getOrderNumberDisplay";
import { Check } from "./types";
import { NotesColumn } from "../reports/NotesColumn";
import Iconify from "../../minimals/components/iconify";
import { exportTableToCsv } from "../taskUtils";
import { DashboardReportType } from "../types/reports";
import ReportBubbles from "../orders/ReportBubbles";

const normalizeOrderNumber = (value: string) => value.trim().toUpperCase();

export default function Checks() {
  const { checks, loading, setDateRange, dateRange, assignedToMe, setAssignedToMe, fetchChecks, setChecks, tableRef, assignedToMyTeam, setAssignedToMyTeam } =
    useContext(ChecksContext);
  const { user } = useContext(UserContext);
  const [teamMemberIds, setTeamMemberIds] = useState<number[]>([]);
  const [assignableInternalUserIds, setAssignableInternalUserIds] = useState<number[]>([]);
  const [isUserManagerOrTME, setIsUserManagerOrTME] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assignedToId = queryParams.get("assignedtoid");
  const isUnassignedOnly = Number(assignedToId) == -1;
  const [assignedToUser, setAssignedToUser] = useState<InternalUser | null>(null);
  const [selectedCheck, setSelectedCheck] = useState<Check | null>(null);
  const [existingOrderNumbers, setExistingOrderNumbers] = useState<Set<string> | null>(null);
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [assignToModalOpen, setAssignToModalOpen] = useState(false);

  const getAndSetAssignedToUser = async () => {
    if (!assignedToId || isUnassignedOnly) {
      return;
    }
    const { data } = await axios.get<InternalUser>(
      `/api/clientphonebook/internalusers/getInternalUserById?id=${assignedToId}`
    );
    setAssignedToUser(data);
  };

  const getInternalUserIdsIncludingPairedUsers = async (ids: number[]) => {
    if (!ids.length) return [];

    const pairedUserQueryParams = new URLSearchParams();
    ids.forEach((id) => pairedUserQueryParams.append("internalUserIds", String(id)));
    const { data } = await axios.get<number[]>(
      `/api/clientphonebook/internalusers/getInternalUserIdsIncludingPairedUsers?${pairedUserQueryParams}`
    );
    return data;
  };

  const getAndSetAssignableInternalUserIds = async () => {
    const id = assignedToId && !isUnassignedOnly ? Number(assignedToId) : user?.id;
    if (!id) {
      setAssignableInternalUserIds([]);
      return;
    }

    const ids = await getInternalUserIdsIncludingPairedUsers([id]);
    setAssignableInternalUserIds(ids);
  };

  const toggleColumnOrdering = () => {
    setEnableOrdering(!enableOrdering);
  };
  const getAndSetInternalUsers = async () => {
    const id = user?.id;
    if (!id) return;
    const { data } = await axios.get<InternalUser[]>(
      `/api/clientphonebook/internalusers/getInternalUserAndTeamMembers?id=${id}`
    );
    if (data) {
      const ids = Array.from(new Set(data.map((u) => u.id)));
      const idsIncludingPairedUsers = await getInternalUserIdsIncludingPairedUsers(ids);
      setTeamMemberIds(idsIncludingPairedUsers);
    }
  };

  const getIfUserIsManagerOrTME = async (id: number) => {
    const { data } = await axios.get<boolean>(
      `/api/clientphonebook/internalusers/getIsInternalUserManagerOrTME?id=${id}`
    );
    setIsUserManagerOrTME(data);
  };

  useEffect(() => {
    if (!user?.id) return;
    getAndSetInternalUsers();
    getIfUserIsManagerOrTME(user.id);
  }, [user?.id]);
  const filteredChecks = useMemo(() => {
    if (isUnassignedOnly) {
      return checks.filter((c) => !c.assignedToUser);
    }

    if (assignedToId) {
      const assignedToIds = assignableInternalUserIds.length ? assignableInternalUserIds : [Number(assignedToId)];
      return checks.filter((c) => !!c.assignedToUser?.id && assignedToIds.includes(c.assignedToUser.id));
    }

    const effectiveAssignedToMyTeam = assignedToMyTeam && isUserManagerOrTME;

    if (!assignedToMe && !effectiveAssignedToMyTeam) return checks;

    return checks.filter((c) => {
      const assignedToIds = assignableInternalUserIds.length ? assignableInternalUserIds : user?.id ? [user.id] : [];
      if (assignedToMe && c.assignedToUser?.id && assignedToIds.includes(c.assignedToUser.id)) return true;
      if (effectiveAssignedToMyTeam && c.assignedToUser != null && teamMemberIds.includes(c.assignedToUser.id)) return true;
      return false;
    });
  }, [assignedToMe, assignedToMyTeam, checks, assignedToId, user?.id, teamMemberIds, isUserManagerOrTME, assignableInternalUserIds]);

  useEffect(() => {
    getAndSetAssignedToUser();
    getAndSetAssignableInternalUserIds();
  }, [assignedToId, user?.id]);

  useEffect(() => {
    const ledgerNames = Array.from(
      new Set(checks.map((check) => check.ledgerName).filter((ledgerName): ledgerName is string => Boolean(ledgerName)))
    );

    if (!ledgerNames.length) {
      setExistingOrderNumbers(new Set());
      return;
    }

    let isCurrent = true;
    setExistingOrderNumbers(null);

    const getAndSetExistingOrderNumbers = async () => {
      try {
        const { data } = await axios.post<string[]>("/api/orders/GetExistingOrderNumbers", ledgerNames);
        if (!isCurrent) {
          return;
        }

        setExistingOrderNumbers(new Set(data.map(normalizeOrderNumber)));
      } catch (error) {
        console.error("Failed to fetch existing order numbers:", error);
        if (isCurrent) {
          setExistingOrderNumbers(null);
        }
      }
    };

    getAndSetExistingOrderNumbers();

    return () => {
      isCurrent = false;
    };
  }, [checks]);

  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };

  const columnsWithNotes = useMemo(
    () => NotesColumn<Check>(getColumns(existingOrderNumbers), setSelectedCheck),
    [existingOrderNumbers]
  );
  return (
    <>
      <Helmet>
        <title> Checks | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl">
        <Box sx={{ mb: 2 }}>
          <ReportBubbles excludedPermissions={["PolicyIssues", "PoliciesToSend"]} />
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h4">
            Outstanding Checks
            {isUnassignedOnly && " - Unassigned"}
            {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
          </Typography>
          <Button component={RouterLink} to="/checks/dead">Dead checks</Button>
        </Stack>
        <MaterialReactTable
          enableStickyHeader
          columns={columnsWithNotes}
          data={filteredChecks}
          enableGlobalFilter
          enableFacetedValues
          enableColumnOrdering={enableOrdering}
          enableRowSelection
          enableRowActions
          autoResetPageIndex={false}
          globalFilterFn="includesString"
          initialState={{
            density: "compact",
            columnFilters: [{ id: "priority", value: ["Urgent", "Important", "To Review"] }],
            pagination: { pageIndex: 0, pageSize: 30 },
            columnVisibility: {
              transactionType: false,
              voidedOn: false,
              status: false,
              extendedMemo: false
            }
          }}
          muiTableBodyProps={{
            sx: {
              "& .MuiTableRow-root:hover .MuiTableCell-root": {
                backgroundColor: "inherit !important"
              }
            }
          }}
          muiTableBodyRowProps={({ row }) => {
            return {
              style: row.index % 2 === 0 ? { backgroundColor: "#f4f4f4" } : {}
            };
          }}
          state={{ showSkeletons: loading }}
          renderTopToolbarCustomActions={({ table }) => {
            return (
              <Box sx={{ flexGrow: 1, position: "relative" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CheckActions
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    assignedToMe={assignedToMe}
                    setAssignedToMe={setAssignedToMe}
                    table={table}
                    hideAssignedToMeOptions={!!assignedToId}
                    onRowsDeselected={onRowsDeselected}
                    assignedToMyTeam={assignedToMyTeam}
                    setAssignedToMyTeam={setAssignedToMyTeam}
                    isUserManagerOrTME={isUserManagerOrTME}
                  />
                  <Box sx={{ position: "absolute", right: 0 }}>
                    <Tooltip title="Column ordering">
                      <IconButton onClick={toggleColumnOrdering}>
                        <Iconify icon="mdi:reorder-vertical" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to csv">
                      <IconButton onClick={() => exportTableToCsv(table, "export.csv")}>
                        <Iconify icon="eva:download-fill" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
              </Box>
            );
          }}
        />
      </Container>
      {!!selectedCheck && (
        <NotesModal
          isOpen
          nameBeforeUpdate={selectedCheck?.assignedToName || ""}
          emailBeforeUpdate={selectedCheck?.assignedToEmail || ""}
          entity={selectedCheck}
          setEntity={setSelectedCheck}
          setEntityTable={setChecks}
          orderNumberDisplay={getOrderNumberDisplay(selectedCheck)}
          amount={fCurrency(selectedCheck?.amount || "")}
          priority={selectedCheck?.priority}
          refetchData={() => fetchChecks({ useLoader: false, resetPagination: false })}
          reportType={DashboardReportType.OutstandingChecks}
        />
      )}
    </>
  );
}
